import { useMarkAsCollectedModal } from '@/context/orders/PharmacistOrdersModals';
import { ListOrdersView } from '@/hooks/orders/useListOrders';
import type { PharmacistClickAndCollectOrder } from '@montugroup/circuit-shared';
import { useControlledDialogProps } from '@montugroup/circuit-shared';
import { getTsPharmacyClient } from '@montugroup/circuit-shared/api/pharmacy.client';
import getAuthBearerToken from '@montugroup/circuit-shared/utils/getAuthBearerToken';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@montugroup/design-system';
import { Grid2, TableHead, useTheme } from '@mui/material';
import type { ReactNode } from 'react';

function TableHeader({ children }: { children: ReactNode }) {
  return (
    <Grid2 size={{ xs: 2 }} sx={{ padding: 2 }} alignSelf={'center'}>
      <TableHead>
        {/* TODO: Remove `as any` when button small variant is typed */}
        <Typography variant={'button small' as any}>{children}</Typography>
      </TableHead>
    </Grid2>
  );
}

function TableBody({ children }: { children: ReactNode }) {
  return (
    <Grid2 size={{ xs: 10 }} alignSelf={'center'}>
      <Typography variant="body1">{children}</Typography>
    </Grid2>
  );
}

function BriefSingleOrderOverview({ order }: { order: PharmacistClickAndCollectOrder }) {
  return (
    <Box>
      <Typography>Confirm this order has been collected.</Typography>
      <Box sx={{ padding: 4, overflowY: 'auto', maxHeight: 300 }}>
        <Grid2 container flexGrow={1}>
          <TableHeader>Patient</TableHeader>
          <TableBody>{order.patient.name}</TableBody>
        </Grid2>
        <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
        <Grid2 container flexGrow={1}>
          <TableHeader>Doctor</TableHeader>
          <TableBody>{order.doctorName}</TableBody>
        </Grid2>
        <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
        <Grid2 container flexGrow={1}>
          <TableHeader>Product</TableHeader>
          <Grid2 size={{ xs: 10 }}>
            {order.orderProducts.map((orderProduct) => {
              return (
                <Box key={orderProduct.id}>
                  <Box
                    sx={{ marginTop: 2, marginBottom: 2 }}
                    display={'flex'}
                    flexDirection={'row'}
                    justifyContent={'space-between'}
                  >
                    <Grid2 container flexGrow={1}>
                      <Grid2 size={{ xs: 1 }}>
                        <Typography variant="body2" fontWeight={'bold'}>
                          {orderProduct.quantity}x
                        </Typography>
                      </Grid2>
                      <Grid2 size={{ xs: 11 }}>
                        <Typography variant="body2">{orderProduct.product.name}</Typography>
                      </Grid2>
                    </Grid2>
                  </Box>
                </Box>
              );
            })}
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
}

const client = getTsPharmacyClient();

export function MarkAsCollectedModal() {
  const { data } = client.listOrders.useQuery({
    queryKey: [...client.listOrders.keys, ListOrdersView.PendingCollection],
    queryData: {
      query: {
        view: ListOrdersView.PendingCollection,
      },
      headers: {
        authorization: getAuthBearerToken(),
      },
    },
  });

  const theme = useTheme();
  const {
    markAsCollectedModal,
    hideMarkAsCollectedModal,
    markOrdersAsCollectedMutation: { mutateAsync, isPending },
  } = useMarkAsCollectedModal();

  const controlledDialogProps = useControlledDialogProps({
    onClose: hideMarkAsCollectedModal,
    open: markAsCollectedModal.visible,
  });

  const handleSubmit = async () => {
    try {
      await mutateAsync({ body: { data: { orderIds: markAsCollectedModal.orderIds! } } });
      controlledDialogProps.onClose();
    } catch (_error) {
      // Do nothing as it's handled in the useMarkAsCollectedModal hook
    }
  };

  if (!markAsCollectedModal.orderIds) {
    return null;
  }

  const isMarkingSingleOrder = markAsCollectedModal.orderIds.length === 1;
  const singleOrder = isMarkingSingleOrder
    ? data?.body.data.ClickAndCollect?.find((order) => order.orderId === markAsCollectedModal?.orderIds?.[0])
    : null;

  return (
    <Dialog {...controlledDialogProps} maxWidth="sm">
      <DialogTitle>
        Marking {markAsCollectedModal.orderIds.length} order{markAsCollectedModal.orderIds.length > 1 ? 's' : ''} as
        collected
      </DialogTitle>
      <DialogContent>
        <Stack direction={'column'} gap={theme.spacing(4)}>
          <Alert severity="info">
            Once you confirm we&apos;ll let your customer{!isMarkingSingleOrder && 's'} know their order
            {!isMarkingSingleOrder && 's'} {!isMarkingSingleOrder ? 'have' : 'has'} been collected.
          </Alert>
          {singleOrder ? (
            <BriefSingleOrderOverview order={singleOrder} />
          ) : (
            <Typography>Please confirm when you&apos;re ready.</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="primary" onClick={controlledDialogProps.onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSubmit()}
          disabled={isPending}
          loading={isPending}
        >
          Confirm collection
        </Button>
      </DialogActions>
    </Dialog>
  );
}
